import { createEffect, createSignal, onCleanup } from "solid-js";
import { type MediaStorageLocation, useChatRuntime } from "../runtime/ChatRuntime";

interface UseSignedImageUrlResult {
  url: () => string | undefined;
  loading: () => boolean;
  error: () => Error | undefined;
}

/**
 * Resolves a signed display URL for a generated image/chart through the
 * runtime seam. Hosts own caching (bluesky's impl consults its URL cache);
 * a host without the seam leaves the loading state terminal-false with an
 * error so cards can show their fallback.
 */
export function useSignedImageUrl(location: () => MediaStorageLocation | undefined): UseSignedImageUrlResult {
  const rt = useChatRuntime();
  const [url, setUrl] = createSignal<string | undefined>();
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<Error | undefined>();

  createEffect(() => {
    let cancelled = false;
    const loc = location();

    if (!loc) {
      setLoading(false);
      return;
    }

    const resolve = rt.actions.resolveImageUrls;
    if (!resolve) {
      setLoading(false);
      setError(new Error("image URLs unavailable on this surface"));
      return;
    }

    setLoading(true);
    setError(undefined);

    const load = async (): Promise<void> => {
      try {
        const results = await resolve([loc]);
        if (cancelled) return;
        const result = results[0];
        if (!result?.url) {
          throw new Error(result?.error || "No signed URL returned from server");
        }
        setUrl(result.url);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(err as Error);
        setLoading(false);
      }
    };
    void load();

    onCleanup(() => {
      cancelled = true;
    });
  });

  return { url, loading, error };
}
